import React from 'react';
import { getFinancials } from '@features/patient-coverages';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';

import { useEligibilityBadgeState } from '../hooks/useEligibilityBadgeState';
import { ELIG_TOKENS } from '../theme/readinessTokens';
import { formatSourceProvenance } from '../utils/provenance';

interface Props {
  patientId?: string;
  encounterId?: string;
}

/**
 * Financials block — the cost picture for this visit. Renders whatever the
 * BE has on `financials`: member-obligation headline, deductible meter, OOP
 * meter, copay, coinsurance, and the BE-supplied benefit_structure line.
 *
 * Hoisted from the experimental EligibilityDrawer's CostSection so the
 * production drawer (and any other detail surface that wants the full cost
 * picture) renders identically.
 */
export const EligibilityFinancialsBlock: React.FC<Props> = ({ patientId, encounterId }) => {
  const { eligibilityData: data } = useEligibilityBadgeState({ patientId, encounterId });
  if (!data) return null;
  const fin = getFinancials(data);

  const hasAnything =
    fin.member_obligation != null ||
    fin.deductible != null ||
    fin.oop_max != null ||
    fin.copayment != null ||
    fin.coinsurance != null;

  if (!hasAnything) return null;

  const provenance = formatSourceProvenance(data.source);

  return (
    <DetailSection title="Cost" provenance={provenance}>
      {fin.member_obligation != null && (
        <Typography
          variant="h4"
          sx={{ color: ELIG_TOKENS.midnight, fontWeight: 600, mb: 0.5 }}
        >
          {dollars(fin.member_obligation)} today
        </Typography>
      )}
      {fin.benefit_structure && (
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft, mb: 1.5 }}>
          {fin.benefit_structure}
        </Typography>
      )}
      {fin.deductible != null && fin.remaining_deductible != null && fin.deductible > 0 && (
        <Meter
          label="Deductible"
          used={fin.deductible - fin.remaining_deductible}
          total={fin.deductible}
        />
      )}
      {fin.oop_max != null && fin.remaining_oop_max != null && fin.oop_max > 0 && (
        <Meter
          label="Out-of-pocket max"
          used={fin.oop_max - fin.remaining_oop_max}
          total={fin.oop_max}
        />
      )}
      <Stack spacing={0} sx={{ mt: 1 }}>
        {fin.copayment != null && <Field label="Copay" value={dollars(fin.copayment)} />}
        {fin.coinsurance != null && (
          <Field label="Coinsurance" value={`${fin.coinsurance}%`} />
        )}
      </Stack>
    </DetailSection>
  );
};

// ─── Internal pieces (also used by EligibilityCoverageBlock) ────────────────

export const DetailSection: React.FC<{
  title: string;
  provenance?: string | null;
  children: React.ReactNode;
}> = ({ title, provenance, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        mb: 1,
      }}
    >
      <Typography variant="overline" sx={{ color: ELIG_TOKENS.midnight, fontWeight: 600 }}>
        {title}
      </Typography>
      {provenance && (
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          {provenance}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

export const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

const Meter: React.FC<{ label: string; used: number; total: number }> = ({
  label,
  used,
  total,
}) => {
  const pct = total > 0 ? Math.max(0, Math.min(100, (used / total) * 100)) : 0;
  return (
    <Box sx={{ py: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.ink, fontWeight: 500 }}>
          {dollars(used)} of {dollars(total)} met
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: ELIG_TOKENS.line,
          '& .MuiLinearProgress-bar': { bgcolor: ELIG_TOKENS.midnight },
        }}
      />
    </Box>
  );
};

function dollars(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value % 1 === 0) return `$${value}`;
  return `$${value.toFixed(2)}`;
}
